import { CampaignType } from '../types/client';

export const campaignTypes: CampaignType[] = [
  // Traffic Campaigns
  {
    id: 'link_clicks',
    name: 'Link Clicks',
    description: 'Drive traffic to your website or landing page',
    objective: 'OUTCOME_TRAFFIC',
    optimizationGoal: 'LINK_CLICKS',
    category: 'traffic',
    icon: '🌐',
    recommendedBudget: {
      min: 50,
      max: 1000,
      suggested: 150
    }
  },
  {
    id: 'landing_page_views',
    name: 'Landing Page Views',
    description: 'Optimize for people who load your landing page',
    objective: 'OUTCOME_TRAFFIC',
    optimizationGoal: 'LANDING_PAGE_VIEWS',
    category: 'traffic',
    icon: '📄',
    recommendedBudget: {
      min: 75,
      max: 1200,
      suggested: 200
    }
  },

  // Awareness Campaigns
  {
    id: 'reach',
    name: 'Brand Awareness',
    description: 'Increase awareness and reach of your brand',
    objective: 'OUTCOME_AWARENESS',
    optimizationGoal: 'REACH',
    category: 'awareness',
    icon: '👁️',
    recommendedBudget: {
      min: 100,
      max: 2000,
      suggested: 300
    }
  },
  {
    id: 'impressions',
    name: 'Impressions',
    description: 'Maximize the number of times your ad is shown',
    objective: 'OUTCOME_AWARENESS',
    optimizationGoal: 'IMPRESSIONS',
    category: 'awareness',
    icon: '📊',
    recommendedBudget: {
      min: 50,
      max: 1500,
      suggested: 200
    }
  },

  // Engagement Campaigns
  {
    id: 'post_engagement',
    name: 'Post Engagement',
    description: 'Get more likes, comments, shares, and clicks',
    objective: 'OUTCOME_ENGAGEMENT',
    optimizationGoal: 'POST_ENGAGEMENT',
    category: 'engagement',
    icon: '❤️',
    recommendedBudget: {
      min: 30,
      max: 800,
      suggested: 100
    }
  },
  {
    id: 'page_likes',
    name: 'Page Likes',
    description: 'Increase followers and likes for your page',
    objective: 'OUTCOME_ENGAGEMENT',
    optimizationGoal: 'PAGE_LIKES',
    category: 'engagement',
    icon: '👍',
    recommendedBudget: {
      min: 40,
      max: 600,
      suggested: 120
    }
  },

  // Conversion Campaigns
  {
    id: 'conversions',
    name: 'Conversions',
    description: 'Drive specific actions on your website',
    objective: 'OUTCOME_SALES',
    optimizationGoal: 'OFFSITE_CONVERSIONS',
    category: 'conversions',
    icon: '🎯',
    recommendedBudget: {
      min: 100,
      max: 3000,
      suggested: 500
    }
  },
  {
    id: 'purchase',
    name: 'Purchase',
    description: 'Optimize for purchases and online sales',
    objective: 'OUTCOME_SALES',
    optimizationGoal: 'OFFSITE_CONVERSIONS',
    category: 'conversions',
    icon: '🛒',
    recommendedBudget: {
      min: 150,
      max: 5000,
      suggested: 600
    }
  },
  {
    id: 'lead_generation',
    name: 'Lead Generation',
    description: 'Collect leads through Facebook forms',
    objective: 'OUTCOME_LEADS',
    optimizationGoal: 'LEAD_GENERATION',
    category: 'conversions',
    icon: '📋',
    recommendedBudget: {
      min: 80,
      max: 2000,
      suggested: 300
    }
  },
  {
    id: 'add_to_cart',
    name: 'Add to Cart',
    description: 'Optimize for people adding items to cart',
    objective: 'OUTCOME_SALES',
    optimizationGoal: 'OFFSITE_CONVERSIONS',
    category: 'conversions',
    icon: '🛍️',
    recommendedBudget: {
      min: 100,
      max: 2500,
      suggested: 400
    }
  },
  {
    id: 'roas',
    name: 'ROAS (Return on Ad Spend)',
    description: 'Optimize for maximum return on advertising spend',
    objective: 'OUTCOME_SALES',
    optimizationGoal: 'VALUE',
    category: 'conversions',
    icon: '💰',
    recommendedBudget: {
      min: 200,
      max: 10000,
      suggested: 800
    },
    requiresROASTarget: true
  },

  // Video Campaigns
  {
    id: 'video_views',
    name: 'Video Views',
    description: 'Maximize video watch time and views',
    objective: 'OUTCOME_ENGAGEMENT',
    optimizationGoal: 'THRUPLAY',
    category: 'video',
    icon: '🎬',
    recommendedBudget: {
      min: 60,
      max: 1200,
      suggested: 200
    }
  },
  {
    id: 'video_engagement',
    name: 'Video Engagement',
    description: 'Get likes, comments, and shares on videos',
    objective: 'OUTCOME_ENGAGEMENT',
    optimizationGoal: 'POST_ENGAGEMENT',
    category: 'video',
    icon: '📹',
    recommendedBudget: {
      min: 70,
      max: 1000,
      suggested: 180
    }
  },
  {
    id: 'video_traffic',
    name: 'Video Traffic',
    description: 'Use video to drive website traffic',
    objective: 'OUTCOME_TRAFFIC',
    optimizationGoal: 'LINK_CLICKS',
    category: 'video',
    icon: '🎥',
    recommendedBudget: {
      min: 80,
      max: 1500,
      suggested: 250
    }
  },

  // Click-to-Messenger Campaign (for conversational sales and leads)
  {
    id: 'click_to_messenger',
    name: 'Click-to-Messenger',
    description: 'Start conversations that lead to sales and purchases',
    objective: 'OUTCOME_TRAFFIC',
    optimizationGoal: 'CONVERSATIONS',
    category: 'conversions',
    icon: '💬',
    recommendedBudget: {
      min: 50,
      max: 1000,
      suggested: 150
    }
  }
];

export const campaignCategories = [
  {
    id: 'traffic',
    name: 'Traffic',
    description: 'Drive visitors to your website',
    icon: '🌐',
    color: 'blue'
  },
  {
    id: 'awareness',
    name: 'Awareness',
    description: 'Increase brand recognition',
    icon: '👁️',
    color: 'purple'
  },
  {
    id: 'engagement',
    name: 'Engagement',
    description: 'Boost social interactions',
    icon: '❤️',
    color: 'pink'
  },
  {
    id: 'conversions',
    name: 'Conversions',
    description: 'Drive sales and leads',
    icon: '🎯',
    color: 'green'
  },
  {
    id: 'video',
    name: 'Video',
    description: 'Promote video content',
    icon: '🎬',
    color: 'red'
  }
];

export const bidStrategies = [
  {
    id: 'LOWEST_COST_WITHOUT_CAP',
    name: 'Lowest Cost',
    description: 'Get the most results for your budget',
    recommended: true
  },
  {
    id: 'LOWEST_COST_WITH_BID_CAP',
    name: 'Lowest Cost with Bid Cap',
    description: 'Control maximum bid amount',
    needsBidAmount: true
  },
  {
    id: 'COST_CAP',
    name: 'Cost Cap',
    description: 'Control average cost per result (recommended for ROAS campaigns)',
    needsBidAmount: true,
    recommended: false
  }
];

export const callToActions = [
  { id: 'LEARN_MORE', name: 'Learn More' },
  { id: 'SHOP_NOW', name: 'Shop Now' },
  { id: 'SIGN_UP', name: 'Sign Up' },
  { id: 'DOWNLOAD', name: 'Download' },
  { id: 'GET_QUOTE', name: 'Get Quote' },
  { id: 'CONTACT_US', name: 'Contact Us' },
  { id: 'BOOK_TRAVEL', name: 'Book Travel' },
  { id: 'LISTEN_MUSIC', name: 'Listen Music' },
  { id: 'WATCH_VIDEO', name: 'Watch Video' },
  { id: 'SUBSCRIBE', name: 'Subscribe' },
  { id: 'APPLY_NOW', name: 'Apply Now' },
  { id: 'REQUEST_TIME', name: 'Request Time' },
  { id: 'SEE_MENU', name: 'See Menu' },
  { id: 'INSTALL_APP', name: 'Install App' },
  { id: 'USE_APP', name: 'Use App' },
  { id: 'PLAY_GAME', name: 'Play Game' },
  { id: 'WATCH_MORE', name: 'Watch More' },
  { id: 'LIKE_PAGE', name: 'Like Page' },
  { id: 'MESSAGE_PAGE', name: 'Message Page' },
  { id: 'SEND_MESSAGE', name: 'Send Message' },
  { id: 'START_CONVERSATION', name: 'Start Conversation' },
  { id: 'GET_SUPPORT', name: 'Get Support' },
  { id: 'BOOK_APPOINTMENT', name: 'Book Appointment' }
];

export const countries = [
  { code: 'US', name: 'United States', flag: '🇺🇸' },
  { code: 'CA', name: 'Canada', flag: '🇨🇦' },
  { code: 'GB', name: 'United Kingdom', flag: '🇬🇧' },
  { code: 'AU', name: 'Australia', flag: '🇦🇺' },
  { code: 'DE', name: 'Germany', flag: '🇩🇪' },
  { code: 'FR', name: 'France', flag: '🇫🇷' },
  { code: 'IT', name: 'Italy', flag: '🇮🇹' },
  { code: 'ES', name: 'Spain', flag: '🇪🇸' },
  { code: 'NL', name: 'Netherlands', flag: '🇳🇱' },
  { code: 'SE', name: 'Sweden', flag: '🇸🇪' },
  { code: 'NO', name: 'Norway', flag: '🇳🇴' },
  { code: 'DK', name: 'Denmark', flag: '🇩🇰' },
  { code: 'FI', name: 'Finland', flag: '🇫🇮' },
  { code: 'BR', name: 'Brazil', flag: '🇧🇷' },
  { code: 'MX', name: 'Mexico', flag: '🇲🇽' },
  { code: 'AR', name: 'Argentina', flag: '🇦🇷' },
  { code: 'CL', name: 'Chile', flag: '🇨🇱' },
  { code: 'IN', name: 'India', flag: '🇮🇳' },
  { code: 'JP', name: 'Japan', flag: '🇯🇵' },
  { code: 'KR', name: 'South Korea', flag: '🇰🇷' },
  { code: 'SG', name: 'Singapore', flag: '🇸🇬' },
  { code: 'MY', name: 'Malaysia', flag: '🇲🇾' },
  { code: 'TH', name: 'Thailand', flag: '🇹🇭' },
  { code: 'PH', name: 'Philippines', flag: '🇵🇭' },
  { code: 'ID', name: 'Indonesia', flag: '🇮🇩' },
  { code: 'VN', name: 'Vietnam', flag: '🇻🇳' }
];

export const interests = [
  // Health & Wellness
  'Health and wellness',
  'Nutrition',
  'Fitness and wellness',
  'Organic food',
  'Natural health',
  'Vitamins and supplements',
  'Weight loss',
  'Yoga',
  'Meditation',
  'Mental health',
  
  // Shopping & Retail
  'Online shopping',
  'E-commerce',
  'Fashion',
  'Beauty',
  'Home and garden',
  'Technology',
  'Electronics',
  'Books',
  'Food and drink',
  'Travel',
  
  // Business & Finance
  'Business',
  'Entrepreneurship',
  'Marketing',
  'Finance',
  'Investing',
  'Real estate',
  'Insurance',
  'Banking',
  
  // Entertainment
  'Movies',
  'Music',
  'Gaming',
  'Sports',
  'Television',
  'Celebrities',
  'Art',
  'Photography',
  
  // Lifestyle
  'Family',
  'Parenting',
  'Education',
  'Cooking',
  'Pets',
  'Cars',
  'Environment',
  'Religion'
]; 